/**
 * AUTOGENERATED FILE. DO NOT EDIT.
 *
 * This file is generated by running yarn i18n:build, do not edit it manually.
 *
 * If you wish to update it, please run yarn i18n:build.
 */
/* eslint-disable no-restricted-imports */
import {i18n} from "@lingui/core";
import * as React from "react";

import {RequestInfo} from "@khan/shared-core";

// We statically import the English strings as we use them as a fallback
// for locales that don't have any translations. Additionally, it improves
// the performance of the English site by not having to load the strings at
// runtime.
// NOTE(john): This is replaced with the actual English strings in the build
// process.
const enStrings = {"uyJsf6":["About"],"k4I7F8":["Impact"],"KM6m8p":["Team"],"smGSLU":["Interns"],"tDE0mP":["Content Specialists"],"HyFz7F":["Leadership"],"3JSu/M":["Supporters"],"cW8Uak":["Translators"],"astDB+":["Blog"],"r5Jw20":["I just learned about ",["name"]," on Khan Academy"],"cCAzz0":["I just earned the ",["desc"]," badge on Khan Academy!"],"bqByWl":["Check it out at ",["url"],"."],"rgx2oJ":["I just earned the ",["desc"]," badge on @khanacademy! Check it out at"],"U1Sf8X":["Continue with Facebook"],"Tk3V+k":["Logging you in..."],"FHJE9I":["Enable Functional Cookies to log in with Facebook"],"Kzy2d8":["More Info"],"sW5OjU":["required"],"LLAa/9":["Optional"],"y5p2bQ":["Did you know?"],"XnGvPC":["Regardless of who you are, mastering even just one more skill on Khan Academy results in learning gains."],"I4wbpi":["Learners have spent ",["actualMinutes"]," billion minutes learning on Khan Academy. ",["approxMinutes"]," billion minutes is equivalent to ",["actualYears"]," years."],"+YjE1r":["You are currently logged in to another Facebook account. Please log out of that account and try again."],"1x3wHL":["Your invitation was not found. Please try clicking the link in your email again."],"yl7Nu2":["Oops! Something went wrong. Please try again."],"/w6LiN":["Let your company double your impact!"],"6YyBGw":["Many companies have a matching gift program — you give an amount and they donate the same. Both donations go straight to Khan Academy."],"Ng063n":["Donate"],"cjFYgc":["If you’re seeing this page, chances are you've already done this step! Thank you so much! If not, please make a donation and be sure to keep your receipt."],"0gVw8B":["Many corporate matching programs will give you up to one year after your donation to request a match."],"0IAEaX":["Match"],"S9Rza1":["Search for your company using the form on the right. Even if nothing comes up, your company may still have a matching program. You can contact your HR department to find out."],"plCbRN":["There are two ways to submit the matching request:"],"UGe+WA":["Download your company's matching gift verification form using the provided link. Print it, fill it out and mail it to us:"],"agCv/K":["Submit a request to your company’s intranet if you find a link to that in the search results."],"m09ge5":["Questions? Email <0>donate@khanacademy.org</0>. Oh, and thanks!"],"YzM1Ed":["Spread the word"],"zw7O/g":["It takes a vibrant community to deliver a free, world-class education for anyone, anywhere.  Tell your friends and family that their gifts to Khan Academy can be matched and inspire them to give, too."],"tQdGl2":["Tweet"],"/7kacT":["I just donated to @KhanAcademy. Join me in supporting a free, world-class education for anyone, anywhere! khanacademy.org/donate"],"bo/56e":["Matching Gifts"]};

let results: Record<string, string | string[]> | undefined = undefined;
let promise: Promise<void> | null = null;

// We keep track of locales that are missing translations so we can load the
// English strings for those locales.
// NOTE(john): This array is replaced with the list of locales.
const missingTranslations: string[] = ["ur","zh-hant","sgn-us","is","en-x-rtl","zu","xh","th","sw","fa","ms","he","fa-af","sq"];

// We keep track of locales that have been bundled together in the same file
// so we can load them all at once.
const extraLocales: string[] = ["te","or","km","sk","rw","mn","lol","fv","ar"];

/**
 * Load the i18n strings for the current locale and add them to the Lingui i18n
 * instance. This uses Suspsense so you'll want to make sure that your
 * component is wrapped in a Suspense (for loading) and an ErrorBoundary
 * (for errors).
 *
 * This function is called by the LoadStrings component, which is a Loadable
 * component that loads the strings for a route and all descendant imports.
 *
 * You can use this directly or you can use the withStrings HoC to load
 * strings for a component.
 */
export const useStrings = () => {
    if (results) {
        return results;
    }

    const locale = RequestInfo.kaLocale;

    // If the locale is English, or doesn't have any translations, we don't need to load
    // any additional strings.
    if (locale === "en" || missingTranslations.includes(locale)) {
        // Load the English strings into Lingui. This serves as a fallback for
        // locales that don't have any translations (and the actual strings for the English
        // locale).
        results = enStrings;
        i18n.load(locale, results);
        return results;
    }

    if (!promise) {
        promise = (async () => {
            const locale = RequestInfo.kaLocale;
            const localeFile = extraLocales.includes(locale) ? "extra" : locale;

            if (process.env.NODE_ENV === "production") {
                const result = (await import(`./lingui/${localeFile}.json`))
                    .default;
                results = localeFile === "extra" ? result[locale] : result;
            } else {
                results = {};
            }

            // Load the strings into Lingui
            i18n.load(locale, {...enStrings, ...results});

            promise = null;
        })();
    }

    throw promise;
};

/**
 * The HOC to load i18n strings for a route and all descendant imports.
 *
 * Examples:
 *
 * - export default withStrings(WrappedComponent);
 *
 * - export default compose(
 *      withStrings,
 *      withRouter
 *   )(WrappedComponent);
 */
export const withStrings = <C extends object>(
    WrappedComponent: React.ComponentType<C>,
) => {
    if (process.env.NODE_ENV === "test") {
        return WrappedComponent;
    }

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component.
    const WrapperComponent = (props: C): React.ReactNode => {
        useStrings();
        return <WrappedComponent {...(props as C)} />;
    };

    WrapperComponent.displayName = `withStrings(${displayName})`;

    return WrapperComponent;
};
